import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Check, Sparkle, Rocket, Coins } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Badge } from "../components/ui/badge";
import SEO from "../components/SEO";

export default function Subscription() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [busy, setBusy] = useState(null);
    const [regional, setRegional] = useState(null);
    const [balance, setBalance] = useState(null);

    useEffect(() => {
        api.get("/payments/unlock-price")
            .then((r) => setRegional(r.data))
            .catch(() => {});
        // Only fetch the user's credit balance when authed — endpoint is gated.
        if (user) {
            api.get("/credits/balance")
                .then((r) => setBalance(r.data))
                .catch(() => {});
        }
    }, [user]);

    const plans = regional?.plans || {};
    const proLabel = plans.pro_monthly?.label || "—";
    const proGlobalLabel = plans.pro_global?.label || "—";
    const boostLabel = plans.boost_post?.label || "—";
    const packLabel = balance?.pack?.label || plans.credits_pack?.label || "$5.00";
    // Canonical subscription tier returned by /payments/unlock-price.
    // Possible: free / pro_local / pro_global / admin. Tied to the
    // server-side `subscription` field; we use it to disable the right
    // CTA so an existing customer doesn't double-pay.
    const tier = regional?.tier || (user?.subscription || "free");
    const isProLocal  = tier === "pro_local";
    const isProGlobal = tier === "pro_global" || tier === "admin";

    const PLANS = [
        {
            id: "free",
            name: "Free",
            price: "Free",
            period: "/ forever",
            description: "Discover ideas and join the community.",
            features: [
                "Sample category in any country",
                "Community Ideas feed",
                "Post your own ideas (free)",
                "Comment, vote & save ideas",
                "5 welcome AI credits (one-time)",
            ],
            cta: "Current plan",
            action: null,
            disabled: true,
        },
        {
            id: "pro_monthly",
            name: "Pro Local",
            price: proLabel,
            period: "· one-time",
            description: "Unlock every admin-curated category in your home country.",
            features: [
                "Everything in Free",
                regional?.country
                    ? `All categories in ${regional.country}`
                    : "All categories in your home country",
                "See contact email of for-sale ideas",
                "Boost your own posts",
                "Early access to new features",
            ],
            cta: "Get Pro Local",
            action: "pro_monthly",
            highlight: true,
            disabled: isProLocal || isProGlobal,
            currentLabel: isProLocal ? "You're on Pro Local ⚡"
                : isProGlobal ? "Covered by Pro Global"
                : null,
        },
        {
            id: "pro_global",
            name: "Pro Global",
            price: proGlobalLabel,
            period: "· one-time",
            description: "Unlock every admin-curated category in every country.",
            features: [
                "Everything in Pro Local",
                "All categories in every country",
                "Browse ideas tailored to other markets",
                "Best value for serious founders & explorers",
            ],
            cta: "Get Pro Global",
            action: "pro_global",
            premium: true,
            disabled: isProGlobal,
            currentLabel: isProGlobal ? "You're on Pro Global ⚡" : null,
        },
    ];

    const checkout = async (plan, postId = null) => {
        // Guest visitors see prices freely but must sign in before paying.
        // This is the path Paddle's compliance reviewer can validate without
        // creating an account: prices visible publicly, purchase guarded.
        if (!user) {
            toast.message("Please sign in to continue with payment.");
            navigate("/auth?next=/subscription");
            return;
        }
        setBusy(plan);
        try {
            const { data } = await api.post("/payments/checkout", {
                plan, post_id: postId,
                origin_url: window.location.origin,
            });
            window.location.href = data.url;
        } catch (e) {
            toast.error("Could not start checkout");
            setBusy(null);
        }
    };

    return (
        <div className="px-4 sm:px-6 lg:px-10 py-8 lg:py-12 max-w-5xl mx-auto" data-testid="subscription-page">
            <SEO
                title="Plans & Pricing — Pro, Boosts & AI Credits"
                description="Upgrade to Ideas Mine Pro to unlock every admin category, boost your posts to the top of the feed for 7 days, or top up AI credits. Regional pricing in GBP, USD, INR and more."
                path="/subscription"
            />
            <header className="mb-10 text-center">
                <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#FF8A00] mb-2">Plans & pricing</div>
                <h1 className="font-display font-black text-4xl lg:text-5xl tracking-tighter brand-text">Pick what fits you</h1>
                <p className="text-muted-foreground mt-2">
                    Prices shown in your local currency
                    {regional?.country ? ` (${regional.country})` : ""}.
                </p>
            </header>

            <div className="grid md:grid-cols-3 gap-5 mb-12">
                {PLANS.map((p) => (
                    <div
                        key={p.id}
                        className={`relative rounded-2xl border ${
                            p.premium  ? "border-[#FF8A00] bg-gradient-to-br from-[#FF8A00]/10 to-[#6C63FF]/10"
                          : p.highlight ? "border-[#6C63FF] bg-[#6C63FF]/5"
                                        : "border-border bg-card"
                        } p-6`}
                        data-testid={`plan-${p.id}`}
                    >
                        {p.highlight && !p.premium && <Badge className="absolute top-4 right-4 bg-[#FF8A00] text-white">Most popular</Badge>}
                        {p.premium && <Badge className="absolute top-4 right-4 bg-[#6C63FF] text-white">Best value</Badge>}
                        <div className="flex items-center gap-2 mb-1">
                            {p.highlight && <Sparkle size={20} weight="fill" className="text-[#FF8A00]" />}
                            {p.premium && <Rocket size={20} weight="fill" className="text-[#FF8A00]" />}
                            <h3 className="font-display font-black text-2xl">{p.name}</h3>
                        </div>
                        <div className="mb-4">
                            <span className="font-display font-black text-4xl" data-testid={`price-${p.id}`}>{p.price}</span>
                            <span className="text-muted-foreground"> {p.period}</span>
                        </div>
                        <p className="text-sm text-muted-foreground mb-5">{p.description}</p>
                        <ul className="space-y-2 mb-6">
                            {p.features.map((f) => (
                                <li key={f} className="flex items-center gap-2 text-sm">
                                    <Check size={16} weight="bold" className="text-[#6C63FF]" /> {f}
                                </li>
                            ))}
                        </ul>
                        {p.action ? (
                            <Button onClick={() => checkout(p.action)}
                                disabled={busy !== null || p.disabled}
                                className={`w-full ${p.highlight || p.premium ? "brand-button" : ""}`}
                                data-testid={`buy-${p.id}`}>
                                {p.currentLabel
                                    ? p.currentLabel
                                    : !user
                                        ? `Sign in to get ${p.name}`
                                        : (busy === p.action ? "Redirecting…" : p.cta)}
                            </Button>
                        ) : (
                            <Button variant="outline" disabled className="w-full" data-testid={`buy-${p.id}`}>
                                {!user ? "Available after sign-up" : "Current plan"}
                            </Button>
                        )}
                    </div>
                ))}
            </div>

            <div className="rounded-2xl border-2 border-[#6C63FF]/30 bg-gradient-to-br from-[#6C63FF]/5 to-[#FF8A00]/5 p-6 mb-5"
                data-testid="ai-credits-card">
                <div className="flex flex-wrap items-start gap-5">
                    <div className="h-14 w-14 rounded-xl bg-[#6C63FF] text-white grid place-items-center shrink-0">
                        <Coins size={26} weight="fill" />
                    </div>
                    <div className="flex-1 min-w-[18rem]">
                        <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#FF8A00] mb-1">
                            AI Chat · Pay-as-you-go
                        </div>
                        <h2 className="font-display font-bold text-2xl tracking-tight">
                            100 credits · {packLabel}
                        </h2>
                        {balance && (
                            <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
                                <span className="inline-flex items-center gap-1.5 font-bold">
                                    <Coins size={13} weight="fill" className={
                                        balance.tokens <= 0
                                            ? "text-red-500"
                                            : balance.tokens < 10000 ? "text-[#FF8A00]" : "text-[#6C63FF]"} />
                                    Your balance:{" "}
                                    <span data-testid="my-credit-balance">
                                        {balance.tokens <= 0
                                            ? "0 credits"
                                            : `${balance.credits} credit${balance.credits === 1 ? "" : "s"} (${balance.tokens.toLocaleString()} tokens)`}
                                    </span>
                                </span>
                            </div>
                        )}
                        <p className="text-xs text-muted-foreground mt-2">
                            See <a href="/terms" className="text-[#6C63FF] font-semibold hover:underline">Terms §6</a> for credit pack details and expiry.
                        </p>
                    </div>
                    <Button
                        onClick={() => checkout("credits_pack")}
                        disabled={busy === "credits_pack"}
                        className="brand-button shrink-0"
                        data-testid="buy-credit-pack"
                    >
                        {busy === "credits_pack" ? "Redirecting…" : `Buy 100 credits — ${packLabel}`}
                    </Button>
                </div>
            </div>

            <div className="rounded-2xl border border-dashed border-border bg-card/40 p-6 flex flex-wrap items-center gap-4">
                <div className="h-12 w-12 rounded-xl bg-[#FF8A00]/10 text-[#FF8A00] grid place-items-center">
                    <Rocket size={22} weight="duotone" />
                </div>
                <div className="flex-1 min-w-[16rem]">
                    <h2 className="font-display font-bold text-lg">Want to boost one of your ideas?</h2>
                    <p className="text-sm text-muted-foreground">
                        Open any idea you posted — you'll see a "Boost · {boostLabel}" button that pins it to the top of the feed for 7 days.
                    </p>
                </div>
            </div>

            {/* Compliance block — referenced by Paddle / Stripe / card-scheme
                reviewers. Kept visible on the public pricing page so the
                merchant-of-record onboarding check finds everything in
                one place: who we are, what we sell, how to get a refund,
                and what currency we charge in. */}
            <section
                className="mt-10 rounded-2xl border border-border bg-card/40 p-6 text-sm text-muted-foreground space-y-3"
                data-testid="pricing-compliance"
            >
                <h2 className="font-display font-bold text-base text-foreground">
                    Billing & legal information
                </h2>
                <p>
                    All purchases on Ideas Mine are <strong className="text-foreground">one-time digital services</strong> delivered
                    immediately on payment success. Pro is a one-time unlock,
                    Boost is a one-time 7-day feature on a single post, and the
                    AI credits pack is a one-time top-up of 100 credits (≈1 M tokens).
                </p>
                <p>
                    Prices are shown in your local currency
                    {regional?.country ? ` (${regional.country})` : ""}{" "}
                    where supported. Payments outside India are processed by
                    Stripe Payments Europe Ltd (Stripe.com). Customers in India
                    are routed to Dodo Payments — a Merchant of Record that
                    accepts UPI, RuPay, NetBanking and Indian-domestic cards
                    natively. You do not need to do anything; the right processor
                    is selected automatically based on your account country.
                </p>
                <p>
                    <strong className="text-foreground">Refunds.</strong> Full details in our{" "}
                    <a href="/refund-policy" className="text-[#6C63FF] font-semibold hover:underline">
                        Refund Policy
                    </a>{" "}
                    (mirrored at{" "}
                    <a href="/terms#refunds" className="text-[#6C63FF] font-semibold hover:underline">
                        Terms §9
                    </a>
                    )
                    {" "}for full details. In short: digital goods are non-refundable
                    once delivered, except as required by UK consumer-rights
                    law. To request a refund or report a problem, email{" "}
                    <a href="mailto:support@ideasmine.co.uk" className="text-[#6C63FF] font-semibold hover:underline">
                        support@ideasmine.co.uk
                    </a>{" "}
                    within 14 days of purchase with your order ID.
                </p>
                <p>
                    Ideas Mine is operated by{" "}
                    <strong className="text-foreground">Inovera Limited</strong>,
                    a private limited company registered in Scotland, United
                    Kingdom (Company No.{" "}
                    <span className="font-mono">SC874398</span>). Registered
                    office address available on request at{" "}
                    <a href="mailto:support@ideasmine.co.uk" className="text-[#6C63FF] font-semibold hover:underline">
                        support@ideasmine.co.uk
                    </a>.
                </p>
            </section>
        </div>
    );
}
